import React, { useState } from 'react'
import { Table, Popover, Tabs } from 'antd'
import { withTheme } from 'styled-components'
import moment from 'moment'
import { InfoCircleOutlined, FilePdfOutlined } from '@ant-design/icons';

import { getPreSignedS3Url } from '../../../Utils/api';
import { saveAs } from 'file-saver'
import { LabeledInput } from '../../Reusable/Input';
import { Text } from '../../Reusable/Text'
import { TextButton } from '../../Reusable/Button';
import { Flex, FlexColumn, LightContainer } from '../../Reusable/Container'
import { MutedTabs } from '../../Reusable/MutedTabs'
import TableView from '../../Reusable/TableView'

const { TabPane } = Tabs

const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
})

const ReportTable = (props) => {
    const [selectedRowId, setSelectedRowId] = useState(null)
    const handleTableChange = (pagination, filters, sorter) => {
        props.fetchReport({ pagination, sorter, })
    }

    // Download Individual Doc
    const downloadDoc = (item) => {
        const urlArr = item.uri.split('.');
        var fileExtension = urlArr[urlArr.length - 1];
        var filename = (item.fileName).includes(fileExtension) ? item.fileName : item.fileName + "." + fileExtension;
        getPreSignedS3Url({ url: item.uri }, async (err, preSignedUrl) => {
            try {
                // prevents opening file in new tab
                let blob = await fetch(preSignedUrl).then((r) => r.blob());
                saveAs(blob, filename);

            } catch (err) {
                console.log(err);
            }
        })
    }

    const getRowDetails = (record, props) => {
        const { theme } = props
        var { id } = record

        const columns = [
            { title: 'Date', dataIndex: 'date', key: 'date', width: 120, render: item => <span style={{ whiteSpace: "nowrap" }}>{moment(item).format("MMM D, YYYY")}</span> },
            { title: 'Description', dataIndex: 'name', key: 'name' },
            { title: 'Amount', dataIndex: 'amount', key: 'amount', width: 120, render: (item) => (formatter.format(item)) },
        ]
        return (
            <MutedTabs key={`${id}-detailsTabs`} size="large" defaultActiveKey="receiptsReceived">
                {
                    (record.payments && record.payments.length > 0) &&
                    <TabPane tab="Receipts Received" key="receiptsReceived">
                        <LightContainer fullWidth>
                            <Flex style={{ width: "100%", padding: "20px", justifyContent: "space-between", alignItems: 'flex-start' }}>
                                <FlexColumn style={{ width: "100%" }}>
                                    <TableView
                                        id="recipt-table"
                                        rowKey="TransactionId"
                                        dataSource={record.payments}
                                        columns={columns}
                                        tableLayout='auto' />
                                </FlexColumn>
                            </Flex>
                        </LightContainer>
                    </TabPane>
                }
                {
                    <TabPane tab="Additional Details" key="additionalDetails">
                        <LightContainer fullWidth>
                            <Flex style={{ width: "100%", padding: "20px", background: theme.body, justifyContent: "space-between", alignItems: 'flex-start' }}>
                                <FlexColumn style={{ width: "100%" }}>
                                    <LightContainer fullWidth>
                                        <Flex start wrap gap='24px'>
                                            <LabeledInput
                                                label='Note'
                                                key={`${id}-note`}
                                                nomargin
                                                type='read-only'
                                                value={record.additionalDetail && record.additionalDetail.note ? record.additionalDetail.note : '--'}
                                            />
                                        </Flex>
                                        <Flex start wrap style={{ marginTop: 10 }}>
                                            <Text color={'#7384AA'} size='14px' margin='0 0 4px' weight={400}>Documents</Text>
                                        </Flex>
                                        <Flex start wrap gap='24px'>
                                            <Flex start>
                                                {
                                                    record && record.additionalDetail && record.additionalDetail.documents ?
                                                        <Flex start style={{ flexWrap: "wrap", gap: 16 }}>
                                                            {
                                                                record && record.additionalDetail && record.additionalDetail.documents
                                                                && record.additionalDetail.documents.map((file, i) => {
                                                                    return (
                                                                        <>
                                                                            <TextButton
                                                                                noBorder height='32px' padding='4px 8px' radius='4px' background='#F8F8F8' key={i} color="#40a9ff"
                                                                                onClick={() => downloadDoc(file)}
                                                                                icon={<FilePdfOutlined style={{ fontSize: 14, color: "#40a9ff" }} />} text={file.fileName} />
                                                                        </>
                                                                    );
                                                                })
                                                            }
                                                        </Flex>
                                                        : '--'
                                                }
                                            </Flex>
                                        </Flex>
                                    </LightContainer>
                                </FlexColumn>
                            </Flex>
                        </LightContainer>
                    </TabPane>
                }
            </MutedTabs>
        )
    }

    const columns = [
        {
            title: 'Date',
            dataIndex: 'reconciliationDate',
            key: 'reconciliationDate',
            render: item => {
                return <span style={{ whiteSpace: "nowrap" }}>{moment(item).format("MMM D, YYYY")}</span>
            },
        },
        {
            title: (
                <Popover content="Total advances deposited">
                    <span style={{ whiteSpace: "nowrap" }}>
                        Advances <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    </span>
                </Popover>
            ),
            dataIndex: 'loanDeposited',
            key: 'loanDeposited',
            render: (item) => (formatter.format(item)),
        },
        {
            title: (
                <Popover content="Payments applied to the line">
                    <span>
                        Receipts <span style={{ whiteSpace: "nowrap" }}>Applied <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} /></span>
                    </span>
                </Popover>
            ),
            dataIndex: 'paymentsReconciled',
            key: 'paymentsReconciled',
            render: (item) => (formatter.format(item)),
        },
        {
            title: (
                <Popover content="Payments applied to principal loan balance">
                    <span>
                        Applied to Principal <span style={{ whiteSpace: "nowrap" }}><InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} /></span>
                    </span>
                </Popover>
            ),
            dataIndex: 'principalApplied',
            key: 'principalApplied',
            render: (item) => (formatter.format(item)),
        },
        {
            title: (
                <Popover content="Payments applied to interest outstanding">
                    <span>
                        Applied to <span style={{ whiteSpace: "nowrap" }}>Interest <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} /></span>
                    </span>
                </Popover>
            ),
            dataIndex: 'interestApplied',
            key: 'interestApplied',
            render: (item) => (formatter.format(item)),
        },
        {
            title: (
                <Popover content="Any refunds post-application of payments">
                    <span style={{ whiteSpace: "nowrap" }}>Refunds <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} /></span>
                </Popover>
            ),
            dataIndex: 'refund',
            key: 'refund',
            render: (item) => (formatter.format(item)),
        },
        {
            title: (
                <Popover content="Ending principal balance after payments are applied">
                    <span>
                        Outstanding Principal <span style={{ whiteSpace: "nowrap" }}><InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} /></span>
                    </span>
                </Popover>
            ),
            dataIndex: 'outstandingLoanBalance',
            key: 'outstandingLoanBalance',
            render: (item) => (formatter.format(item)),
        },
        {
            title: (
                <Popover content="Ending interest balance after payments are applied">
                    <span>
                        Outstanding <span style={{ whiteSpace: "nowrap" }}>Interest <InfoCircleOutlined style={{ marginLeft: 4, color: 'rgba(0,0,0,.45)' }} /></span>
                    </span>
                </Popover>
            ),
            dataIndex: 'totalOutstandingInterestToDate',
            key: 'totalOutstandingInterestToDate',
            render: (item) => (formatter.format(item)),
        },
        {
            title: (
                <Popover content="Daily interest on ending outstanding principal.">
                    <span>
                        Daily <span style={{ whiteSpace: "nowrap" }}>Interest <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} /></span>
                    </span>
                </Popover>
            ),
            dataIndex: 'dailyInterest',
            key: 'dailyInterest',
            render: (item) => (formatter.format(item)),
        },
    ]

    const collateralColumns = [
        {
            title: 'Date',
            dataIndex: 'reconciliationDate',
            key: 'reconciliationDate',
            render: item => moment(item).format("MMM D, YYYY"),
        },
        {
            title: (
                 <Popover content="Total accounts receivable at the snapshot time, before any ineligibility rules are applied">
                    <span style={{ whiteSpace: "nowrap" }}>
                        Gross AR <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    </span>
                </Popover>
            ),
            dataIndex: 'grossAR',
            key: 'grossAR',
            render: (item) => (formatter.format(item)),
        },
        {
            title: (
                <Popover content="Accounts receivable eligible for borrowing at the snapshot time, prior to applying adjustments. Computed based on Advance Rate agreed upon in your contract">
                    <span style={{ whiteSpace: "nowrap" }}>
                        Gross Advanceable AR <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    </span>
                </Popover>
            ),
            dataIndex: 'grossAdvanceableAR',
            key: 'grossAdvanceableAR',
            render: (item) => (formatter.format(item)),
        },
        {
            title: (
                <Popover content="Total inventory value recorded as of the snapshot time, without eligibility considerations">
                    <span style={{ whiteSpace: "nowrap" }}>
                        Gross Inventory <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    </span>
                </Popover>
            ),
            dataIndex: 'grossInventory',
            key: 'grossInventory',
            render: (item) => (formatter.format(item)),
        },
        {
            title: (
                <Popover content="Inventory considered eligible for borrowing at the snapshot time, before any adjustments. Computed based on Advance Rate agreed upon in your contract">
                    <span style={{ whiteSpace: "nowrap" }}>
                        Gross Advanceable Inventory <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    </span>
                </Popover>
            ),
            dataIndex: 'grossAdvanceableInventory',
            key: 'grossAdvanceableInventory',
            render: (item) => (formatter.format(item)),
        },
        {
            title: (
                <Popover content="Total adjustments applied to Advanceable collateral">
                    <span style={{ whiteSpace: "nowrap" }}>
                        Adjustment <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    </span>
                </Popover>
            ),
            dataIndex: 'adjustments',
            key: 'adjustments',
            render: (item) => (formatter.format(item)),
        },
        {
            title: (
                <Popover content="Advanceable collateral value (AR plus Inventory) available for borrowing after all adjustments have been applied">
                    <span style={{ whiteSpace: "nowrap" }}>
                        Net Advanceable Assets <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    </span>
                </Popover>
            ),
            dataIndex: 'netAdvanceableAssets',
            key: 'netAdvanceableAssets',
            render: (item) => (formatter.format(item)),
        },
        {
            title: (
                <Popover content="Amount borrowed against the collateral at the snapshot time">
                    <span style={{ whiteSpace: "nowrap" }}>
                        Outstanding Balance <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    </span>
                </Popover>
            ),
            // dataIndex: 'outstandingLoanBalance',
            // key: 'outstandingLoanBalance',
            render: (item) => (item.outstandingLoanBalanceOfDepositedLoans ? formatter.format(item.outstandingLoanBalanceOfDepositedLoans) : formatter.format(item.outstandingLoanBalance)),
        },
        {
            title: (
                <Popover content="Amount by which the outstanding balance exceeds net advanceable assets as of the snapshot time">
                    <span style={{ whiteSpace: "nowrap" }}>
                        Over Advanced Amount <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                    </span>
                </Popover>
            ),
            dataIndex: 'overAdvancedAmount',
            key: 'overAdvancedAmount',
            render: (item) => (formatter.format(item)),
        },
    ]

    let count = 0
    const data = props.reports.map(x => {
        return {
            key: count++, ...x,
        }
    })

    console.log("d", props.reports);

    return (
        <>
            <TableView
                id="report-table"
                titleText={props.titleText}
                rowKey="id"
                dataSource={data}
                columns={props.componentType != "CollateralHistory" ? columns : collateralColumns}
                tableLayout='auto'
                pagination={props.pagination}
                onChange={handleTableChange}
                pageSize={props.pageSize || 50}
                onLoadMore={props.onLoadMore}
                moreToLoad={props.moreToLoad}
                loading={props.loading}
                loadingMore={props.loading}
                onRow={
                    (props.expand != false) ?  // ON by default
                        (record, rowIndex) => {
                            return {
                                onClick: event => {
                                    if (record['id'] == selectedRowId) {
                                        setSelectedRowId(null)
                                    } else {
                                        setSelectedRowId(record['id'])
                                        getRowDetails(record, props)
                                    }
                                }, // click row
                            }
                        }
                        :
                        () => { }
                }
                expand={props.expand != false} // ON by default
                expandedRowKey={selectedRowId}
                getRowDetails={(record) => getRowDetails(record, props)}
                ctaContent={props.ctaContent}
            />
        </>
    )
}

export default withTheme(ReportTable)